import { AnimatePresence, motion } from 'motion/react'
import type { AddressBookEntry } from './addressBookHelpers'
import type { AddressLabels } from './addressBookConfig'
import { LocationIcon } from './addressBookConfig'
import {
  CustomerAreaSurfaceCard,
  EmptyStateBlock,
  customerMotionTransition,
} from '../customer-area'

type AddressBookListSectionProps = {
  addressBook: AddressBookEntry[]
  labels: AddressLabels
  countryLabelMap: Map<string, string>
  saving: boolean
  reducedMotion: boolean
  onCreateAddress: () => void
  onEditAddress: (entry: AddressBookEntry) => void
  onRemoveAddress: (entryId: string) => void
  onSetDefaultRole: (entryId: string, role: 'shipping' | 'billing') => void
}

export const AddressBookListSection = ({
  addressBook,
  labels,
  countryLabelMap,
  saving,
  reducedMotion,
  onCreateAddress,
  onEditAddress,
  onRemoveAddress,
  onSetDefaultRole,
}: AddressBookListSectionProps) => (
  <CustomerAreaSurfaceCard
    title={labels.title}
    subtitle={labels.subtitle}
    actions={
      <motion.button
        type="button"
        onClick={onCreateAddress}
        whileTap={reducedMotion ? undefined : { scale: 0.985 }}
        transition={customerMotionTransition}
        className="ca-btn-primary inline-flex h-9 items-center px-3 text-sm font-semibold"
      >
        {labels.addAddress}
      </motion.button>
    }
  >
    <AnimatePresence mode="wait" initial={false}>
      {addressBook.length === 0 ? (
        <motion.div
          key="addresses-empty"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={customerMotionTransition}
        >
          <EmptyStateBlock
            icon={<LocationIcon className="h-4 w-4" />}
            title={labels.emptyTitle}
            description={labels.emptyDescription}
            action={
              <motion.button
                type="button"
                onClick={onCreateAddress}
                whileTap={reducedMotion ? undefined : { scale: 0.985 }}
                transition={customerMotionTransition}
                className="ca-btn-primary inline-flex h-9 items-center px-3 text-sm font-semibold"
              >
                {labels.emptyCta}
              </motion.button>
            }
          />
        </motion.div>
      ) : (
        <motion.ul
          key="addresses-list"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={customerMotionTransition}
          className="grid gap-4 lg:grid-cols-2"
        >
          {addressBook.map((entry) => {
            const countryLabel = countryLabelMap.get(entry.country) ?? entry.country
            const fullName = [entry.firstName, entry.lastName].filter(Boolean).join(' ')
            const cityLine = [entry.postalCode, entry.city].filter(Boolean).join(' ')
            const locationLine = [cityLine, entry.province ? `(${entry.province})` : '', countryLabel]
              .filter(Boolean)
              .join(' ')

            return (
              <motion.li
                key={entry.id}
                whileHover={reducedMotion ? undefined : { y: -1, scale: 1.01 }}
                whileTap={reducedMotion ? undefined : { scale: 0.995 }}
                transition={customerMotionTransition}
                className="ca-card-shell ca-theme-transition rounded-xl px-4 py-3.5 transition-[transform,border-color,box-shadow] duration-200 hover:border-(--color-border-strong) hover:shadow-(--shadow-md)"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-(--color-text-primary)">{fullName || '-'}</p>
                    {entry.label ? <p className="mt-0.5 text-xs text-(--color-text-muted)">{entry.label}</p> : null}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {entry.useShipping ? (
                      <span className="ca-badge-info inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        {labels.shippingBadge}
                      </span>
                    ) : null}
                    {entry.useBilling ? (
                      <span className="ca-badge-neutral inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        {labels.billingBadge}
                      </span>
                    ) : null}
                    {entry.defaultShipping || entry.defaultBilling ? (
                      <span className="ca-badge-success inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        {labels.defaultBadge}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2 space-y-0.5 text-sm text-(--color-text-secondary)">
                  <p>{entry.addressLine || '-'}</p>
                  <p>{locationLine || '-'}</p>
                  <p className="text-xs text-(--color-text-muted)">{entry.phone || labels.noPhone}</p>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-(--color-border-default) pt-3">
                  <motion.button
                    type="button"
                    onClick={() => onEditAddress(entry)}
                    whileTap={reducedMotion ? undefined : { scale: 0.985 }}
                    transition={customerMotionTransition}
                    className="ca-btn-secondary inline-flex h-8 items-center px-2.5 text-xs font-semibold"
                  >
                    {labels.edit}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => onRemoveAddress(entry.id)}
                    disabled={saving}
                    whileTap={reducedMotion || saving ? undefined : { scale: 0.985 }}
                    transition={customerMotionTransition}
                    className="ca-btn-secondary inline-flex h-8 items-center border-(--color-status-error-border) text-(--color-status-error) hover:border-(--color-status-error-border) hover:bg-(--color-status-error-bg) px-2.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {labels.remove}
                  </motion.button>

                  {entry.defaultShipping ? (
                    <span className="text-xs font-medium text-(--color-text-secondary)">{labels.defaultShippingState}</span>
                  ) : (
                    <motion.button
                      type="button"
                      onClick={() => onSetDefaultRole(entry.id, 'shipping')}
                      disabled={saving}
                      whileTap={reducedMotion || saving ? undefined : { scale: 0.985 }}
                      transition={customerMotionTransition}
                      className="ca-btn-secondary inline-flex h-8 items-center px-2.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {labels.setDefaultShipping}
                    </motion.button>
                  )}

                  {entry.defaultBilling ? (
                    <span className="text-xs font-medium text-(--color-text-secondary)">{labels.defaultBillingState}</span>
                  ) : (
                    <motion.button
                      type="button"
                      onClick={() => onSetDefaultRole(entry.id, 'billing')}
                      disabled={saving}
                      whileTap={reducedMotion || saving ? undefined : { scale: 0.985 }}
                      transition={customerMotionTransition}
                      className="ca-btn-secondary inline-flex h-8 items-center px-2.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {labels.setDefaultBilling}
                    </motion.button>
                  )}
                </div>
              </motion.li>
            )
          })}
        </motion.ul>
      )}
    </AnimatePresence>
  </CustomerAreaSurfaceCard>
)
